import React, { useEffect, useState } from 'react';
import Modal from '../../General/Modal';
import { useUrlDataContext } from '../lib/UrlDataContext';
import { getUrlDataOverrides, updateUrlDataOverrides } from '../../../api/url_data';
import cogoToast from 'cogo-toast';
import './UrlDataOverrides.scss';
import { useSelector } from 'react-redux';
import Loader from '../../General/Loader';
import { ADMIN_SCOPES } from '../../../constants/admin_scope_constants';
import { hasScope } from '../../../helpers/scope_helpers';

export const UrlDataOverrides = () => {
  const currentUser = useSelector(state => state.users.currentUser);
  const adminScopes = currentUser?.scopes || [];

  const { currentUrlData, isCurrentUrlBlocked } = useUrlDataContext();

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [existingUrlOverrides, setExistingUrlOverrides] = useState();
  const [existingDomainOverrides, setExistingDomainOverrides] = useState();
  const [urlOverrides, setUrlOverrides] = useState({});
  const [domainOverrides, setDomainOverrides] = useState({});

  const saveUrlOverrides = async e => {
    e.preventDefault();
    setIsUpdating(true);
    // Every rendered field is sent; null clears that override key server-side.
    const updates = { forcePrice: null, forceCurrency: null, forceIsInStock: null };
    const { forcePrice, forceCurrency, forceIsInStock } = urlOverrides;
    if (forcePrice) {
      const newPrice = Number.parseFloat(forcePrice);
      if (isNaN(newPrice)) {
        cogoToast.error('Price override is invalid');
        setIsUpdating(false);
        return;
      }
      updates.forcePrice = newPrice;
    }

    if (forceCurrency && forceCurrency.length !== 3) {
      cogoToast.error('Currency override must be a 3-letter currency code like USD or EUR');
      setIsUpdating(false);
      return;
    }
    if (forceCurrency) updates.forceCurrency = forceCurrency.toUpperCase();

    // The select holds strings once touched ('true'/'false'/'') and booleans straight from the stored JSON.
    if (forceIsInStock === true || forceIsInStock === 'true') updates.forceIsInStock = true;
    else if (forceIsInStock === false || forceIsInStock === 'false') updates.forceIsInStock = false;

    try {
      await updateUrlDataOverrides(currentUrlData.domain, currentUrlData.urlStem, currentUrlData.hashedUrlStem, updates);
      cogoToast.success('Updated URL overrides');
    } catch (err) {
      cogoToast.error(err);
    }

    setIsUpdating(false);
  };

  const saveDomainOverrides = async e => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      await updateUrlDataOverrides(currentUrlData.domain, undefined, undefined, domainOverrides);
      cogoToast.success('Updated domain overrides');
    } catch (err) {
      cogoToast.error(err);
    }

    setIsUpdating(false);
  };

  // load existing overrides — refetched per open so paging to another URL can't save stale state
  useEffect(() => {
    if (!showModal) return;
    setIsLoading(true);
    setDomainOverrides({});
    Promise.all([
      getUrlDataOverrides(currentUrlData.domain, currentUrlData.hashedUrlStem).then(data => {
        setExistingUrlOverrides(JSON.parse(data.overrides?.overrides ?? '{}'));
        setUrlOverrides(JSON.parse(data.overrides?.overrides ?? '{}'));
      }),
      getUrlDataOverrides(currentUrlData.domain).then(data => setExistingDomainOverrides(JSON.parse(data.overrides?.overrides ?? '{}')))
    ])
      .catch(err => cogoToast.error(err))
      .finally(() => setIsLoading(false));
  }, [showModal, currentUrlData.hashedUrlStem]);

  if (!hasScope(adminScopes, ADMIN_SCOPES.MANAGE_CATALOG)) {
    return null;
  }

  return (
    <>
      <div>
        <button className='overrides-button' onClick={() => setShowModal(true)} disabled={isCurrentUrlBlocked}>
          Overrides
        </button>
      </div>

      {showModal && (
        <Modal close={() => setShowModal(false)}>
          <div className='overrides-modal'>
            <header>
              <h1>Override Data</h1>
              <p>Override this URL's information, or set rules for all URLs from this domain</p>
            </header>

            {isLoading ? (
              <Loader />
            ) : (
              <>
                <section>
                  <h3>URL Overrides</h3>
                  <p>Override information only for this specific URL</p>
                  <form onSubmit={saveUrlOverrides}>
                    <label>
                      Price
                      <input
                        type='text'
                        value={urlOverrides.forcePrice ?? existingUrlOverrides.forcePrice ?? ''}
                        onChange={e => {
                          const newPrice = e.target.value;
                          setUrlOverrides(prev => ({ ...prev, forcePrice: newPrice }));
                        }}
                      />
                    </label>
                    <label>
                      Currency
                      <input
                        type='text'
                        value={urlOverrides.forceCurrency ?? existingUrlOverrides.forceCurrency ?? ''}
                        onChange={e => {
                          const newCurrency = e.target.value;
                          setUrlOverrides(prev => ({ ...prev, forceCurrency: newCurrency }));
                        }}
                      />
                    </label>
                    <label>
                      Availability
                      <select
                        value={urlOverrides.forceIsInStock ?? existingUrlOverrides.forceIsInStock ?? ''}
                        onChange={e => {
                          const newStock = e.target.value;
                          setUrlOverrides(prev => ({ ...prev, forceIsInStock: newStock }));
                        }}
                      >
                        <option value=''></option>
                        <option value='true'>In Stock</option>
                        <option value='false'>Out of Stock</option>
                      </select>
                    </label>
                    <button type='submit' disabled={isUpdating}>
                      Save
                    </button>
                  </form>
                </section>

                <section>
                  <h3>Domain Overrides</h3>
                  <p>
                    Override information for all <b>{currentUrlData.domain}</b> URLs
                  </p>
                  <form onSubmit={saveDomainOverrides}>
                    <label>
                      Hide all prices
                      <input
                        type='checkbox'
                        checked={domainOverrides.hidePrices ?? existingDomainOverrides.hidePrices ?? false}
                        onChange={e => {
                          const newValue = e.target.checked;
                          setDomainOverrides(prev => ({ ...prev, hidePrices: newValue }));
                        }}
                      />
                    </label>
                    <button type='submit' disabled={isUpdating}>
                      Save
                    </button>
                  </form>
                </section>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};
